import { useEffect, useState } from "react";
import { getPublicCountyStats } from "../api";

interface CountyStats {
  county_id: string;
  county_name: string;
  state: string;
  total_active: number;
  status_breakdown: Record<string, number>;
  top_zip_codes: Array<{ zip_code: string; count: number }>;
  updated_at?: string;
}

interface AllStatsResponse {
  counties: CountyStats[];
  generated_at: string;
}

/**
 * Fetch the live total active property count from the public county-stats endpoint.
 * Returns { count, loading, error }.
 *
 * If the fetch fails, returns a fallback value with isStale=true so the UI
 * can display a loading state, error message, or gracefully degrade.
 */
export function useTrackedPropertiesCount() {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Public, unauthenticated endpoint — routed through the shared apiClient
    // (not a bare fetch) so it carries X-Device-Id (account-sharing signal,
    // #434) and the 429 rate-limit toast like every other API call. apiClient
    // only attaches Authorization when a session exists, so this is safe for
    // logged-out visitors (see apiClient.ts's request interceptor).
    getPublicCountyStats<AllStatsResponse>()
      .then((payload) => {
        // Sum total_active across all counties
        const total = payload.counties.reduce((sum, c) => sum + (c.total_active || 0), 0);
        setCount(total);
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e));
        setLoading(false);
      });
  }, []);

  return { count, loading, error };
}
